import React from 'react';
import { AlertTriangle } from 'lucide-react';
import MultiRoleCell from './MultiRoleCell';
import Tooltip from '@components/common/Tooltip';
import { ScheduleItem, ScheduleItemContract } from '@app-types';
import { UserPermissions } from '@contexts/AuthContext';
import styles from './SchedulerTableCells.module.css';

// Get open conflicts for a contract
const getOpenConflicts = (contract: ScheduleItemContract) => {
  return (contract.conflicts || []).filter((c) => c.status !== 'resolved');
};

// Helper: Get manager conflict tooltip content
const getManagerConflictTooltip = (conflicts: Array<{ conflict_type_name: string }>): string => {
  if (conflicts.length === 0) return '';
  if (conflicts.length === 1) return conflicts[0].conflict_type_name;
  return `${conflicts.length} manager conflicts`;
};

const ContractManagerCell: React.FC<{
  scheduleItem: ScheduleItem;
  contract: ScheduleItemContract;
  isGroupRow: boolean;
  isChildRow: boolean;
  permissions: UserPermissions;
  onAssignRole: (
    item: ScheduleItem,
    contractId: number,
    role: 'owner' | 'manager' | 'assessor',
    isGroupOperation?: boolean
  ) => void;
}> = ({ scheduleItem, contract, isGroupRow, isChildRow, permissions, onAssignRole }) => {
  const openConflicts = getOpenConflicts(contract);
  const hasManagerConflict = openConflicts.some((c) => c.conflict_type_code === 'CM_MISMATCH');
  const managerConflicts = openConflicts.filter((c) => c.conflict_type_code === 'CM_MISMATCH');
  const tooltipContent = getManagerConflictTooltip(managerConflicts);

  // For parent row with conflict, show all unique managers from all contracts
  let displayManagers = contract.contract_managers;
  if (isGroupRow && !isChildRow && hasManagerConflict) {
    const allManagers = scheduleItem.contracts.flatMap((c) => c.contract_managers);
    const uniqueManagers = Array.from(new Map(allManagers.map((m) => [m.id, m])).values());
    displayManagers = uniqueManagers;
  }

  return (
    <div className={styles.managerCellWrapper}>
      <MultiRoleCell
        assignees={displayManagers}
        roleType="manager"
        scheduleItem={scheduleItem}
        contractId={contract.id}
        permissions={permissions}
        onAssignRole={onAssignRole}
        isChildRow={isChildRow}
        isGroupRow={isGroupRow}
        hasConflict={hasManagerConflict}
      />
      {hasManagerConflict && !isChildRow && (
        <Tooltip content={tooltipContent} position="top" variant="error">
          <span className={styles.conflictWarningIcon}>
            <AlertTriangle size={16} />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default ContractManagerCell;
